import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { PlatformActivity } from '@/api/platformMetrics/platformMetricsApi.types';
import {
  CategoryBarChart,
  type CategoryBarDatum,
} from '@/components/common/charts/CategoryBarChart';
import { ChartCard } from '@/components/common/charts/ChartCard';
import { usePlatformActivityQuery } from '@/hooks/usePlatformChartsQueries';
import { resolveSeriesColor } from '@/theme/chartPalette';

interface ActivityByActionChartProps {
  isEnabled: boolean;
}

export function ActivityByActionChart({ isEnabled }: ActivityByActionChartProps) {
  const { t } = useTranslation('admin');
  const activityQuery = usePlatformActivityQuery(isEnabled);
  const activity = activityQuery.data;
  const data = activity === undefined ? [] : buildActionData(activity, t);

  return (
    <ChartCard
      title={t('dashboard.charts.byAction.title')}
      iconName="BarChart"
      isLoading={activityQuery.isLoading === true}
      isError={activityQuery.isError === true}
      onRetry={function retry() {
        activityQuery.refetch();
      }}
      isEmpty={data.length === 0}
      emptyLabel={t('dashboard.charts.byAction.empty')}
    >
      <CategoryBarChart
        data={data}
        orientation="bars"
        accessibleLabel={t('dashboard.charts.byAction.title')}
        formatValue={formatWholeNumber}
        valueLabel={t('dashboard.charts.byAction.eventsLabel')}
        categoryAxisWidth={150}
      />
    </ChartCard>
  );
}

function buildActionData(activity: PlatformActivity, translate: TFunction): CategoryBarDatum[] {
  return activity.byAction.map(function toDatum(actionCount, index) {
    return {
      key: actionCount.action,
      label: translate('activity:action.' + actionCount.action, {
        defaultValue: translate('activity:action.unknown'),
      }),
      value: actionCount.count,
      color: resolveSeriesColor(index),
    };
  });
}

function formatWholeNumber(value: number): string {
  return String(value);
}
